import { useAtomValue } from "jotai";
import { log, logE } from "../../../shared/services/debug-logger";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getVoyagePerformanceReportsMock,
  VOYAGE_EVALUATION_DASHBOARD_MOCK,
} from "../mocks/voyage-evaluation.mocks";
import {
  DEFAULT_VOYAGE_PERFORMANCE_FILTERS,
  EMPTY_VOYAGE_EVALUATION_LANDING_FILTERS,
  EMPTY_VOYAGE_PERFORMANCE_FILTERS,
  type VoyageEvaluationDashboard,
  type VoyageEvaluationLandingFilters,
  type VoyageEvaluationSummary,
  type VoyageEvaluationView,
  type VoyagePerformanceFilters,
  type VoyagePerformanceReport,
  type VoyagePerformanceReportsResponse,
} from "../models/voyage-evaluation.models";
import {
  getVoyageEvaluationDashboard,
  getVoyageEvaluationErrorMessage,
  getVoyagePerformanceReports,
} from "../services/voyage-evaluation.service";
import {
  filterVoyageEvaluationSummaries,
  filterVoyagePerformanceReports,
} from "../utils/voyage-evaluation-selectors.utils";
import { voyageEvaluationMockEnabledAtom } from "./voyage-evaluation.atoms";

// Owns both recovered bookmark views, their independent filters, and request cleanup.
// Landing filters and the selected voyage survive bookmark navigation just like the Power BI report.
export const useVoyageEvaluationDashboard = (canLoad: boolean) => {
  const useMockData = useAtomValue(voyageEvaluationMockEnabledAtom);
  const [dashboard, setDashboard] = useState<VoyageEvaluationDashboard | null>(null);
  const [view, setView] = useState<VoyageEvaluationView>("landing");
  const [landingFilters, setLandingFilters] = useState<VoyageEvaluationLandingFilters>(EMPTY_VOYAGE_EVALUATION_LANDING_FILTERS);
  const [performanceFilters, setPerformanceFilters] = useState<VoyagePerformanceFilters>(DEFAULT_VOYAGE_PERFORMANCE_FILTERS);
  const [selectedVoyage, setSelectedVoyage] = useState<VoyageEvaluationSummary | null>(null);
  const [performanceResponse, setPerformanceResponse] = useState<VoyagePerformanceReportsResponse | null>(null);
  const [selectedPerformanceReport, setSelectedPerformanceReport] = useState<VoyagePerformanceReport | null>(null);
  const [steamingHoursBeforeReportSelection, setSteamingHoursBeforeReportSelection] = useState<{ min: string; max: string } | null>(null);
  const [isDashboardLoading, setIsDashboardLoading] = useState(canLoad);
  const [isPerformanceLoading, setIsPerformanceLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [performanceError, setPerformanceError] = useState<string | null>(null);

  useEffect(() => {
    setView("landing");
    setSelectedVoyage(null);
    setPerformanceResponse(null);
    setPerformanceError(null);
    setLandingFilters(EMPTY_VOYAGE_EVALUATION_LANDING_FILTERS);
    setPerformanceFilters(DEFAULT_VOYAGE_PERFORMANCE_FILTERS);
    setSelectedPerformanceReport(null);
    setSteamingHoursBeforeReportSelection(null);
  }, [useMockData]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    if (!canLoad) {
      setIsDashboardLoading(false);

      return () => controller.abort();
    }

    const loadDashboard = async () => {
      log("STATE - loadDashboard - loading", { mock: useMockData });
      setIsDashboardLoading(true);
      setDashboardError(null);

      try {
        const response = useMockData
          ? VOYAGE_EVALUATION_DASHBOARD_MOCK
          : await getVoyageEvaluationDashboard(controller.signal);

        if (active) {
          log("STATE - loadDashboard - apply", { count: response.voyages.length });
          setDashboard(response);
        }
      } catch (error) {
        if (active && !controller.signal.aborted) {
          logE(new Error("STATE - loadDashboard - failed"));
          setDashboard(null);
          setDashboardError(getVoyageEvaluationErrorMessage(error));
        }
      } finally {
        if (active) setIsDashboardLoading(false);
      }
    };

    void loadDashboard();

    return () => {
      active = false;
      controller.abort();
    };
  }, [canLoad, useMockData]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    if (!canLoad || view !== "performance" || !selectedVoyage) {
      setIsPerformanceLoading(false);

      return () => controller.abort();
    }

    const loadPerformanceReports = async () => {
      log("STATE - loadPerformanceReports - loading", { voyageNo: selectedVoyage.voyage });
      setIsPerformanceLoading(true);
      setPerformanceError(null);
      setPerformanceResponse(null);

      try {
        const response = useMockData
          ? getVoyagePerformanceReportsMock(selectedVoyage.imo, selectedVoyage.voyage)
          : await getVoyagePerformanceReports(selectedVoyage.imo, selectedVoyage.voyage, controller.signal);

        if (active) {
          log("STATE - loadPerformanceReports - apply", { count: response.reports.length });
          setPerformanceResponse(response);
        }
      } catch (error) {
        if (active && !controller.signal.aborted) {
          logE(new Error("STATE - loadPerformanceReports - failed"));
          setPerformanceResponse(null);
          setPerformanceError(getVoyageEvaluationErrorMessage(error));
        }
      } finally {
        if (active) setIsPerformanceLoading(false);
      }
    };

    void loadPerformanceReports();

    return () => {
      active = false;
      controller.abort();
    };
  }, [canLoad, selectedVoyage, useMockData, view]);

  const filteredVoyages = useMemo(
    () => filterVoyageEvaluationSummaries(dashboard?.voyages ?? [], landingFilters),
    [dashboard, landingFilters],
  );
  const filteredReports = useMemo(
    () => filterVoyagePerformanceReports(performanceResponse?.reports ?? [], performanceFilters),
    [performanceFilters, performanceResponse],
  );

  const setLandingFilter = useCallback((name: keyof VoyageEvaluationLandingFilters, value: string) => {
    setLandingFilters((current) => ({ ...current, [name]: value }));
  }, []);
  const setPerformanceFilter = useCallback((name: keyof VoyagePerformanceFilters, value: string) => {
    setPerformanceFilters((current) => ({ ...current, [name]: value }));
    setSelectedPerformanceReport(null);
    setSteamingHoursBeforeReportSelection(null);
  }, []);
  const openPerformance = useCallback(() => {
    log("STATE - openPerformance");
    setView("performance");
  }, []);
  const selectVoyage = useCallback((voyage: VoyageEvaluationSummary) => {
    log("STATE - selectVoyage", { imoNumber: voyage.imo, voyageNo: voyage.voyage });
    setSelectedVoyage(voyage);
    setSelectedPerformanceReport(null);
    setSteamingHoursBeforeReportSelection(null);
  }, []);
  const selectPerformanceReport = useCallback((report: VoyagePerformanceReport | null) => {
    log("STATE - selectPerformanceReport", { selected: !!report });
    if (!report) {
      setSelectedPerformanceReport(null);
      setPerformanceFilters((current) => steamingHoursBeforeReportSelection ? {
        ...current,
        steamingHoursMin: steamingHoursBeforeReportSelection.min,
        steamingHoursMax: steamingHoursBeforeReportSelection.max,
      } : current);
      setSteamingHoursBeforeReportSelection(null);

      return;
    }

    if (!steamingHoursBeforeReportSelection) {
      setSteamingHoursBeforeReportSelection({
        min: performanceFilters.steamingHoursMin,
        max: performanceFilters.steamingHoursMax,
      });
    }
    setPerformanceFilters((current) => {
      if (report.steamingHours === null) return current;

      const selectedHours = String(report.steamingHours);

      return { ...current, steamingHoursMin: selectedHours, steamingHoursMax: selectedHours };
    });
    setSelectedPerformanceReport(report);
  }, [performanceFilters.steamingHoursMax, performanceFilters.steamingHoursMin, steamingHoursBeforeReportSelection]);
  const clearAllFilters = useCallback(() => {
    log("STATE - clearAllFilters");
    setLandingFilters(EMPTY_VOYAGE_EVALUATION_LANDING_FILTERS);
    setPerformanceFilters(EMPTY_VOYAGE_PERFORMANCE_FILTERS);
    setSelectedPerformanceReport(null);
    setSteamingHoursBeforeReportSelection(null);
  }, []);
  const restorePerformanceDefaults = useCallback(() => {
    log("STATE - restorePerformanceDefaults");
    setPerformanceFilters((current) => ({
      ...DEFAULT_VOYAGE_PERFORMANCE_FILTERS,
      condition: current.condition,
      goodWeather: current.goodWeather,
      isZnEnriched: current.isZnEnriched,
    }));
    setSelectedPerformanceReport(null);
    setSteamingHoursBeforeReportSelection(null);
  }, []);

  return {
    dashboard,
    view,
    landingFilters,
    performanceFilters,
    selectedVoyage,
    selectedPerformanceReport,
    performanceResponse,
    filteredVoyages,
    filteredReports,
    isDashboardLoading,
    isPerformanceLoading,
    dashboardError,
    performanceError,
    useMockData,
    setLandingFilter,
    setPerformanceFilter,
    setSelectedVoyage: selectVoyage,
    setSelectedPerformanceReport: selectPerformanceReport,
    openPerformance,
    backToLanding: () => {
      log("STATE - backToLanding");
      setView("landing");
    },
    clearAllFilters,
    restorePerformanceDefaults,
  };
};
